import React, { type FC, useCallback, useMemo } from "react";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDateValue,
    BusinessDivisionValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import type { FinishedGoodsReceipt } from "../../../../api/finishedGoodsReceipts";
import { StockFinishedGoodsReceiptContentsList } from "./components/StockFinishedGoodsReceiptContentsList/StockFinishedGoodsReceiptContentsList";
import { StockFinishedGoodsReceiptScanPlaceholder } from "./components/StockFinishedGoodsReceiptScanPlaceholder/StockFinishedGoodsReceiptScanPlaceholder";
import { useStockFinishedGoodsReceiptCardQuery, useStockFinishedGoodsReceiptContentsQuery } from "./hooks";
import { useStockFinishedGoodsReceiptCardMutations } from "./useStockFinishedGoodsReceiptCardMutations";
import { useStockFinishedGoodsReceiptCancelConfirm } from "./useStockFinishedGoodsReceiptCancelConfirm";
import { stockFinishedGoodsReceiptCardWindows } from "./stockFinishedGoodsReceiptCard.windows";
import "./StockFinishedGoodsReceiptCard.scss";

const STOCK_FINISHED_GOODS_RECEIPT_CARD_SCAN_PAUSE_WINDOW_KEYS = [stockFinishedGoodsReceiptCardWindows.content.key];

/** Карточка приемки готовой продукции на СГП по ВЗН УП. */
export const StockFinishedGoodsReceiptCard: FC = () => {
    const inplantCode = useRequiredNumberParam("code", "код ВЗН УП");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockFinishedGoodsReceiptCardQuery(inplantCode);
    const inplant = cardQuery.data;
    const contentsQuery = useStockFinishedGoodsReceiptContentsQuery(inplantCode, inplant?.ReceiverCode ?? 0);
    const avOperationsQuery = useBoAvOperations(inplant?.Code, inplant != null);
    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_FINISHED_GOODS_RECEIPT_CARD_SCAN_PAUSE_WINDOW_KEYS);

    const mutations = useStockFinishedGoodsReceiptCardMutations({
        onContentRefetch: (contentCode) => contentsQuery.refetchContentByCode(contentCode),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndCancel } = useStockFinishedGoodsReceiptCancelConfirm({
        perform: mutations.cancelPlacementBySdbMutation.mutateAsync,
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentInplant: FinishedGoodsReceipt) => {
        return {
            operation,
            boCode: currentInplant.Code,
            documentLabel: `ВЗН УП №${currentInplant.Num}`,
            fields: [
                {
                    label: "Отправитель",
                    value: formatDivisionText(currentInplant.Sender, currentInplant.SenderSection),
                },
                {
                    label: "Получатель",
                    value: formatDivisionText(currentInplant.Receiver, currentInplant.ReceiverSection),
                },
                { label: "Статус", value: formatBoStateText(currentInplant.Bo?.State) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: inplant,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        if (operationItems.length === 0) {
            return [];
        }

        return [
            {
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            },
        ];
    }, [operationItems]);

    const openContentWindow = useCallback(
        (contentCode: number): void => {
            if (inplant == null) {
                return;
            }

            stockFinishedGoodsReceiptCardWindows.content.open(windows, {
                inplantCode,
                divisionCode: inplant.ReceiverCode,
                contentCode,
                placeBySdbMutation: mutations.placeBySdbMutation,
                confirmAndCancelPlacement: confirmAndCancel,
                // TODO(overlay): isPending здесь — снимок при open; в окне не реактивен. См. prop JSDoc.
                isCancelPlacementPending: mutations.cancelPlacementBySdbMutation.isPending,
            });
        },
        [
            confirmAndCancel,
            inplant,
            inplantCode,
            mutations.cancelPlacementBySdbMutation.isPending,
            mutations.placeBySdbMutation,
            windows,
        ],
    );

    if (cardQuery.isFetching || inplant == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_finished_goods_receipt_card_page">
            <CardHeader
                text={`Приход на СГП по ВЗН УП №${inplant.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList
                className="stock_finished_goods_receipt_card_inform"
                layout="tiles"
                columns="auto"
                minWidth={96}
                oneLine
            >
                <DetailList.Row label="Статус ВЗН" value={<BusinessBoStateValue state={inplant.Bo?.State} />} />
                <DetailList.Row
                    label="Отправитель"
                    value={<BusinessDivisionValue division={inplant.Sender} section={inplant.SenderSection} />}
                />
                <DetailList.Row
                    label="Получатель"
                    value={<BusinessDivisionValue division={inplant.Receiver} section={inplant.ReceiverSection} />}
                />
                <DetailList.Row label="Дата ВЗН" value={<BusinessDateValue value={inplant.DocDate} />} />
            </DetailList>
            <StockFinishedGoodsReceiptContentsList contentsQuery={contentsQuery} onOpenContent={openContentWindow} />
            <StockFinishedGoodsReceiptScanPlaceholder
                contents={contentsQuery.content}
                enabled={!contentsQuery.isFetching && !isCardScannerSuppressedByWindow}
                onOpenContent={openContentWindow}
            />
        </div>
    );
};
